import { useCallback, useContext } from 'react';
import { LecturePageMode } from 'lecture_pages/components';
import QuestionSetContext, { QuestionContext } from 'lecture_pages/services/question-set-context';
import { ProgressiveQuizMode } from 'quizzes/components/mode';
import { ComponentKey, ComponentType } from 'redux/schemas/models/lecture-component';
import { QuizQuestionType } from 'redux/schemas/models/quiz';
import { getActivityType } from 'redux/selectors/timeline';
import { gray7 } from 'styles/global_defaults/colors';
import { config } from '../../../config/config.json';


const useQuestionSetHelpers = () => {
  const {
    mode,
    lecturePageMode,
    isEditQuestionMode,
    lectureComponent,
    currentQuestionResponse,
  } = useContext(QuestionSetContext);

  const {
    currentQuestion,
  } = useContext(QuestionContext);

  const isCurrentActivityType = useCallback((type: ComponentType) => (lectureComponent.type === type), [lectureComponent.type]);

  const isEditMode = isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)
    ? mode === ProgressiveQuizMode.EDIT
    : !!isEditQuestionMode;

  const getAnswerInputBackgroundColor = () => (
    currentQuestionResponse && isCurrentActivityType(ComponentType.SURVEY)
      ? gray7
      : 'transparent'
  );

  const getFeedbackElementId = (currentQuestionId: number) => `question_feedback_${currentQuestionId}`;
  const getQuestionLabelElementId = (currentQuestionId: number) => `question_label_${currentQuestionId}`;

  const isAnswerMode = isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)
    ? mode === ProgressiveQuizMode.ANSWER
    : lecturePageMode === LecturePageMode.VIEW;

  const isRestrictedEditMode = lecturePageMode === LecturePageMode.RESTRICTED_EDIT;
  const isReorderMode = lecturePageMode === LecturePageMode.REORDER;
  const isLinkedEditMode = lecturePageMode === LecturePageMode.LINKED_EDIT;

  const isReviewMode = mode === ProgressiveQuizMode.REVIEW;
  const isMultipleChoiceQuestion = currentQuestion?.type === QuizQuestionType.MULTIPLE_CHOICE
    || currentQuestion?.type === QuizQuestionType.MULTIPLE_CHOICE_MULTIPLE_ANSWER;
  const isMultipleAnswer = currentQuestion?.type === QuizQuestionType.MULTIPLE_CHOICE_MULTIPLE_ANSWER;
  const isStatement = currentQuestion?.type === QuizQuestionType.STATEMENT;
  const isShortAnswerQuestion = currentQuestion?.type === QuizQuestionType.SHORT_TEXT_QUESTION
    || currentQuestion?.type === QuizQuestionType.ALL_QUESTION_VIEW_SHORT_TEXT_QUESTION;
  const isNumberAnswerQuestion = currentQuestion?.type === QuizQuestionType.NUMBER_QUESTION;
  const isLongAnswerQuestion = currentQuestion?.type === QuizQuestionType.LONG_ANSWER_QUESTION;
  const isLikertAnswerQuestion = currentQuestion?.type === QuizQuestionType.LIKERT_SCALE_QUESTION;
  const isMatchingQuestion = currentQuestion?.type === QuizQuestionType.MATCHING_QUESTION;

  const showAnswerFeedback = !isCurrentActivityType(ComponentType.SURVEY);

  const dataQaQuestionSetAttributes = config.pendo.activities[ComponentKey[getActivityType(lectureComponent.type)]];

  return {
    isEditMode,
    isAnswerMode,
    isReviewMode,
    isMultipleChoiceQuestion,
    isMultipleAnswer,
    isStatement,
    isShortAnswerQuestion,
    isNumberAnswerQuestion,
    isLongAnswerQuestion,
    isLikertAnswerQuestion,
    isRestrictedEditMode,
    isReorderMode,
    isLinkedEditMode,
    isMatchingQuestion,
    isCurrentActivityType,
    getFeedbackElementId,
    getQuestionLabelElementId,
    getAnswerInputBackgroundColor,
    dataQaQuestionSetAttributes,
    showAnswerFeedback,
  };
};

export default useQuestionSetHelpers;
